import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Check, Zap, ExternalLink, CreditCard } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { api } from '../../services/api'

export default function BillingPage() {
  const { user, syncUserProfile } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loadingPortal, setLoadingPortal] = useState(false)
  const [loadingUpgrade, setLoadingUpgrade] = useState(false)

  // Al volver del checkout de Stripe, sincronizar el plan desde el backend
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      syncUserProfile().finally(() => {
        setSearchParams({}, { replace: true })
      })
    }
  }, [])

  const isPro = user?.plan === 'pro'

  const handlePortal = async () => {
    setLoadingPortal(true)
    try {
      const { url } = await api.getBillingPortal()
      window.location.href = url
    } catch {
      alert('No se pudo abrir el portal de facturación. Intenta de nuevo.')
    } finally {
      setLoadingPortal(false)
    }
  }

  const handleUpgrade = async () => {
    setLoadingUpgrade(true)
    try {
      const priceId = import.meta.env.VITE_STRIPE_PRICE_MONTHLY || 'price_1TJfxlEH9OGCTDAu7LYSXkmU'
      const { url } = await api.createCheckoutSession(priceId)
      window.location.href = url
    } catch {
      alert('No se pudo iniciar el proceso de pago. Intenta de nuevo.')
    } finally {
      setLoadingUpgrade(false)
    }
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>Facturación</h1>
        <p className="text-sm mt-1" style={{ color: '#64748B' }}>
          Gestiona tu suscripción y método de pago
        </p>
      </div>

      {/* Current plan */}
      <div className="glass-card p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold" style={{ color: '#E2E8F0' }}>Plan actual</h2>
          {isPro ? (
            <span
              className="text-xs font-bold px-3 py-1 rounded-full"
              style={{ background: 'linear-gradient(135deg, #4F46E5, #8B5CF6)', color: 'white' }}
            >
              Pro
            </span>
          ) : (
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{ background: 'rgba(255,255,255,0.07)', color: '#94A3B8' }}
            >
              Free
            </span>
          )}
        </div>

        {isPro ? (
          <>
            <p className="text-sm mb-1" style={{ color: '#94A3B8' }}>
              Tienes acceso completo a todas las funciones de Kapri.
            </p>
            <ul className="space-y-2 mt-4">
              {[
                'Materiales ilimitados (100 pág, 25MB)',
                '3 niveles de dificultad en quizzes',
                'Resúmenes con 3 niveles de detalle',
                'Explicaciones con citas del material',
                'Progreso comparativo entre intentos',
                'Dashboard para padres (hasta 5 hijos)',
                'Historial permanente',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: '#CBD5E1' }}>
                  <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#10B981' }} />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handlePortal}
              disabled={loadingPortal}
              className="btn-secondary mt-6 flex items-center gap-2 disabled:opacity-60"
            >
              <ExternalLink className="w-4 h-4" />
              {loadingPortal ? 'Abriendo portal...' : 'Gestionar suscripción'}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              Estás en el plan gratuito. Actualiza a Pro para desbloquear todas las funciones.
            </p>

            <div
              className="mt-4 rounded-xl p-4"
              style={{ background: 'rgba(79,70,229,0.08)', border: '1px solid rgba(79,70,229,0.2)' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4" style={{ color: '#818CF8' }} />
                <span className="text-sm font-semibold" style={{ color: '#A5B4FC' }}>
                  Kapri Pro
                </span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base line-through" style={{ color: '#64748B' }}>$24.99</span>
                <span className="text-2xl font-bold" style={{ color: '#F1F5F9' }}>$14.95</span>
                <span className="text-sm mb-0.5" style={{ color: '#64748B' }}>/mes</span>
              </div>
              <p className="text-xs" style={{ color: '#475569' }}>
                Cancelá cuando quieras
              </p>
            </div>

            <button
              onClick={handleUpgrade}
              disabled={loadingUpgrade}
              className="btn-primary mt-4 flex items-center gap-2 disabled:opacity-60"
              style={{ boxShadow: '0 0 20px rgba(79,70,229,0.4)' }}
            >
              <Zap className="w-4 h-4" />
              {loadingUpgrade ? 'Redirigiendo...' : 'Actualizar a Pro'}
            </button>
          </>
        )}
      </div>

      {/* Payment method note */}
      {isPro && (
        <div className="glass-card p-5">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <CreditCard className="w-4 h-4" style={{ color: '#64748B' }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: '#E2E8F0' }}>
                Método de pago
              </p>
              <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>
                Administra tu tarjeta y facturas desde el portal de Stripe
              </p>
            </div>
            <button
              onClick={handlePortal}
              disabled={loadingPortal}
              className="ml-auto text-xs flex items-center gap-1 disabled:opacity-60"
              style={{ color: '#818CF8' }}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Abrir portal
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
